import { Platform } from 'react-native'

export const toFixedNoRounding = (num: number = 0, fixed: number): string => {
  // verify is a number
  num = parseFloat(String(num))

  if (isNaN(num)) {
    return '0.00' // return '0.00' if num is not a valid number
  }

  if (fixed !== undefined) {
    return num.toFixed(fixed)
  } else {
    return num.toFixed(2)
  }
}

export const changeToCurrency = (value: number) => {
  const unmaskedValue = String(value.toFixed(2)).replace(/[^0-9]/g, '')

  const formattedValue = (Number(unmaskedValue) / 100)
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')

  return `R$ ${formattedValue}`
}

export const currencyFormat = (value: string | number) => {
  if (Platform.OS === 'web') {
    return Number(value)
      .toLocaleString('pt-br', {
        style: 'currency',
        currency: 'BRL'
      })
      .replace('R$', '')
  } else {
    let valueFormatted = String(value)

    valueFormatted = valueFormatted?.replace(/(\d)(\d{2})$/, '$1,$2').replace('.', ',')

    valueFormatted = valueFormatted?.replace(/(?=(\d{3})+(\D))\B/g, '.')

    value = valueFormatted

    return value
  }
}

export const currencyFormatShortCut = (value: string) => {
  if (!value) return '0'

  const formatValue = String(value)?.replace('.00', '') ?? ''

  if (formatValue.length <= 6) return `${formatValue?.slice(0, 3)}Mil`
  else if (formatValue.length >= 7) return `${formatValue?.slice(0, 2).split('').join('.')}M`

  return value
}

export const currencyFormatChart = (
  value: string = '0',
  needsLabel: boolean,
  shortcut?: boolean,
  toFixed?: number
) => {
  const valueFormatted = String(parseFloat(toFixedNoRounding(Number(value), 2)))

  const precisionValue = valueFormatted?.includes('.')
    ? valueFormatted?.split('.')[1]
    : valueFormatted

  const integerValue = valueFormatted?.includes('.')
    ? valueFormatted?.split('.')[0]
    : valueFormatted

  const integerValueLength = integerValue.length

  if (shortcut) {
    if (Number(valueFormatted) < 10 && Number(precisionValue) > 0)
      return `${toFixedNoRounding(Number(valueFormatted), 2)}`
    else if (integerValueLength < 3) return `${toFixedNoRounding(Number(valueFormatted), 0)}`
    else if (integerValueLength < 6)
      return needsLabel
        ? `${toFixedNoRounding(Number(valueFormatted) / 1000, toFixed ?? 0)}Mil`
        : `${toFixedNoRounding(Number(valueFormatted) / 1000, toFixed ?? 0)}`
    else if (integerValueLength < 9)
      return needsLabel
        ? `${toFixedNoRounding(Number(valueFormatted) / 1000000, 2)}M`
        : `${toFixedNoRounding(Number(valueFormatted) / 1000000, 2)}`
    else if (integerValueLength < 12)
      return needsLabel
        ? `${toFixedNoRounding(Number(valueFormatted) / 1000000000, 2)}B`
        : `${toFixedNoRounding(Number(valueFormatted) / 1000000000, 2)}`
    else
      return needsLabel
        ? `${toFixedNoRounding(Number(valueFormatted) / 1000000000000, 2)}T`
        : `${toFixedNoRounding(Number(valueFormatted) / 1000000000000, 2)}`
  } else {
    if (Number(valueFormatted) < 10 && Number(precisionValue) > 0)
      return `${toFixedNoRounding(Number(valueFormatted), toFixed ?? 2)}`
    else if (Number(valueFormatted) === 0) return `${toFixedNoRounding(Number(valueFormatted), 0)}`
    else if (integerValueLength < 4)
      return `${toFixedNoRounding(Number(valueFormatted), toFixed ?? 0)}`
    else if (integerValueLength < 7)
      return needsLabel
        ? `${toFixedNoRounding(Number(valueFormatted) / 1000, toFixed ?? 0)}Mil`
        : `${toFixedNoRounding(Number(valueFormatted) / 1000, toFixed ?? 0)}`
    else if (integerValueLength < 10)
      return needsLabel
        ? `${toFixedNoRounding(Number(valueFormatted) / 1000000, 2)}M`
        : `${toFixedNoRounding(Number(valueFormatted) / 1000000, 2)}`
    else if (integerValueLength < 13)
      return needsLabel
        ? `${toFixedNoRounding(Number(valueFormatted) / 1000000000, 2)}B`
        : `${toFixedNoRounding(Number(valueFormatted) / 1000000000, 2)}`
    else
      return needsLabel
        ? `${toFixedNoRounding(Number(valueFormatted) / 1000000000000, 2)}T`
        : `${toFixedNoRounding(Number(valueFormatted) / 1000000000000, 2)}`
  }
}

export const currencyFormatProposals = (
  value: string | number = '0',
  needsLabel: boolean,
  shortcut?: boolean,
  toFixed: number = 2,
  noRound?: boolean
) => {
  const num = Number(value)

  if (isNaN(num)) return '0'

  const abs = Math.abs(num)

  const sign = num < 0 ? '-' : ''

  const formatNumber = (n: number, suffix: string) => {
    const formatted = noRound ? n.toFixed(toFixed) : toFixedNoRounding(n, toFixed)

    return needsLabel ? `${formatted}${suffix}` : formatted
  }

  if (shortcut) {
    if (abs < 1000)
      return (
        sign +
        (noRound
          ? abs.toFixed(abs < 10 && abs % 1 !== 0 ? 2 : 0)
          : toFixedNoRounding(abs, abs < 10 && abs % 1 !== 0 ? 2 : 0))
      )

    if (abs < 1000000) return sign + formatNumber(abs / 1000, '\u00A0Mil')

    if (abs < 1000000000) return sign + formatNumber(abs / 1000000, '\u00A0M')

    if (abs < 1000000000000) return sign + formatNumber(abs / 1000000000, '\u00A0B')

    return sign + formatNumber(abs / 1000000000000, '\u00A0T')
  }

  if (abs < 1000)
    return (
      sign +
      (noRound
        ? abs.toFixed(abs < 10 && abs % 1 !== 0 ? 2 : 0)
        : toFixedNoRounding(abs, abs < 10 && abs % 1 !== 0 ? 2 : 0))
    )

  if (abs < 1000000) return sign + formatNumber(abs / 1000, '\u00A0Mil')

  if (abs < 1000000000) return sign + formatNumber(abs / 1000000, '\u00A0M')

  if (abs < 1000000000000) return sign + formatNumber(abs / 1000000000, '\u00A0B')

  return sign + formatNumber(abs / 1000000000000, '\u00A0T')
}
